import { z } from 'zod';
import {
  birthdateSchema,
  emailSchema,
  firstNameSchema,
  identificationSchema,
  lastNameSchema,
  phoneSchema,
  phoneOptionalSchema
} from './common/schemas';

const userSchema = (props: { minAge?: number; maxAge?: number }) =>
  z.object({
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    identification: identificationSchema,
    birthdate: birthdateSchema(props),
    location: z.string().min(1, 'schemas.common.fieldRequired'),
    phone: phoneOptionalSchema,
    email: z
      .string()
      .email('schemas.common.emailValid')
      .or(z.literal(''))
      .optional()
  });

const parentsSchema = z
  .object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    phone: z.string().optional(),
    email: z.string().optional(),
    address: z.string().optional(),
    location: z.string().optional(),
    postalCode: z.string().optional(),
    connection: z.string().optional(),
    identification: z
      .object({
        number: z.string().optional(),
        type: z.string().optional()
      })
      .optional()
  })
  .superRefine((data, ctx) => {
    const {
      firstName,
      lastName,
      phone,
      email,
      address,
      location,
      postalCode,
      identification
    } = data;

    const values = [
      firstName,
      lastName,
      phone,
      email,
      address,
      location,
      postalCode,
      identification?.type,
      identification?.number
    ];

    const isEmpty = values.every((val) => !val || val === '');

    if (isEmpty) return; // Nada que validar

    const entries = [
      { key: 'firstName', value: firstName },
      { key: 'lastName', value: lastName },
      { key: 'phone', value: phone },
      { key: 'email', value: email },
      { key: 'identification.type', value: identification?.type },
      { key: 'identification.number', value: identification?.number }
    ];

    for (const entry of entries) {
      if (!entry.value || entry.value.length < 2) {
        if (entry.key === 'email') {
          ctx.addIssue({
            path: entry.key.split('.'),
            message: 'schemas:common.emailRequired',
            code: z.ZodIssueCode.custom
          });
        } else {
          ctx.addIssue({
            path: entry.key.split('.'),
            message: 'schemas:common.fieldRequired',
            code: z.ZodIssueCode.custom
          });
        }
      }
    }
  });

const emergencyContactSchema = z
  .object({
    fullName: z.string().optional(),
    phone: phoneOptionalSchema.optional()
  })
  .superRefine((data, ctx) => {
    if ((data.fullName && !data.phone) || (!data.fullName && data.phone)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'schemas.common.fieldRequired'
      });
    }
  });

const formActivityConsumerSchema = ({
  minAge,
  maxAge
}: {
  minAge?: number;
  maxAge?: number;
}) => {
  return z.object({
    consumer: userSchema({ minAge, maxAge }),
    emergencyContact: emergencyContactSchema,
    parents: z.array(parentsSchema),
    additionalData: z.object({
      illness: z
        .object({
          value: z.boolean().optional(),
          answer: z.string().optional()
        })
        .superRefine((data, ctx) => {
          if (data.value === true && !data.answer) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['answer'],
              message: 'schemas.common.fieldRequired'
            });
          }
        }),
      acceptDataPrivacy: z.boolean().optional(),
      acceptTakePhotos: z.boolean().optional(),
      authOfferProducts: z.boolean().optional(),
      group: z.string().optional().nullable()
    }),
    observations: z.string().optional()
  });
};

export default formActivityConsumerSchema;
